import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  useTheme,
  Tooltip,
  MenuItem,
  Select,
  Tabs,
  Tab,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HistoryIcon from '@mui/icons-material/History';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { useDealerGroup } from 'app/context/DealerGroupContext';
import { useRole } from 'app/context/RoleContext';
import SelectMAIQ from 'app/components/SelectMAIQ';
import ConfirmationModal from '../ConfirmationModal';
import { useUser } from 'app/context/UserContext';
import { formatToLocalTime } from 'utils/dateUtils';
import EventLogs from './eventLogs';
import TabsMAIQ from 'app/components/TabsMAIQ';
import PasswordResetSection from './PasswordResetSection';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { headerSaga } from 'app/pages/Header/saga';
import { headerActions, reducer, sliceKey } from 'app/pages/Header/slice';
import { selectDealersList } from 'app/pages/Header/selectors';

interface UserDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  open,
  onClose,
  data,
  setRefreshKey,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState(data);
  const [originalData, setOriginalData] = useState(data);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0); // 0 for All dealerships, 1 for Dealership Lists
  const [validateFields, setValidateFields] = useState<(() => boolean) | null>(null);
  const { editUser, deleteUser } = useUser();

  // Reset edit state when modal closes
  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setUser(data);
      setOriginalData(data);
      setValidateFields(null);
    }
  }, [open, data]);

  const handleCancelEdit = () => {
    setUser(originalData);
    setIsEditing(false);
  };

  const handleEditSubmit = () => {
    // Validate required fields if validation function is available
    if (validateFields && !validateFields()) {
      return; // Stop submission if validation fails
    }

    setIsSubmitting(true);

    // Prepare user data based on the selection
    let userData: any = { ...user };

    // Check if current role is dealership_executive or ai_dealership_executive
    
    const isDealershipExecutive =
      user.roles &&
      user.roles.length > 0 &&
      (user.roles[0] === 'dealership_executive' || user.roles[0] === 'ai_dealership_executive');

    // For dealership_executive role, determine which field to send
    if (isDealershipExecutive) {
      if (selectedTab === 0 && user.dealer_id) {
        // "All dealerships" tab selected - send dealer ID
        userData = {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          zip: user.zip,
          status: user.status,
          roles: user.roles,
          dealer_id: user.dealer_id,
        };
      } else if (selectedTab === 1 && user.dealer_group_id) {
        // "Dealership Lists" tab selected - send dealer_group_id
        userData = {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          zip: user.zip,
          status: user.status,
          roles: user.roles,
          dealer_group_id: user.dealer_group_id,
        };
      }
    } else {
      // For other roles, always send dealer_group_id
      userData = {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        zip: user.zip,
        status: user.status,
        roles: user.roles,
        dealer_group_id: user.dealer_group_id,
      };
    }

    editUser(user._id, userData, () => {
      setRefreshKey(prev => prev + 1);
      setIsEditing(false);
      setIsSubmitting(false);
      onClose();
    });
    setOpenConfirm(false);
  };

  const handleDeleteUser = () => {
    setIsSubmitting(true);
    deleteUser(user._id, () => {
      setRefreshKey(prev => prev + 1);
      onClose();
      setIsSubmitting(false);
    });
    setOpenDeleteConfirm(false);
  };

  const tabsData = [
    {
      label: 'USER DETAILS',
      content: (
        <UserDetailsTab
          open={open}
          onClose={onClose}
          data={data}
          setRefreshKey={setRefreshKey}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          user={user}
          setUser={setUser}
          originalData={originalData}
          setOriginalData={setOriginalData}
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirm}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          openDeleteConfirm={openDeleteConfirm}
          setOpenDeleteConfirm={setOpenDeleteConfirm}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          onValidateAndSubmit={handleEditSubmit}
          onSetValidateFunction={(fn) => setValidateFields(() => fn)}
        />
      ),
    },
    { label: 'EVENT LOGS', content: <EventLogs userId={data._id} /> },
  ];

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: '800px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleAltIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                User Details
              </Typography>
            </Box>
            <Box>
              <Tooltip title={isEditing ? 'Editing in progress' : 'Edit User'}>
                <IconButton
                  onClick={() => {
                    if (!isEditing) {
                      setIsEditing(true);
                      setOriginalData(user);
                    }
                  }}
                  sx={{
                    color: 'white',
                    p: 0.5,
                    opacity: isEditing ? 0.5 : 1,
                    cursor: isEditing ? 'not-allowed' : 'pointer',
                  }}
                  size="small"
                  disabled={isEditing}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete User (Disabled)">
                <IconButton
                  onClick={() => setOpenDeleteConfirm(true)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    p: 0.5,
                    ml: 1,
                    cursor: 'not-allowed',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                  size="small"
                  disabled
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                    ml: 1,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <TabsMAIQ tabs={tabsData} />
          </Box>

          {/* Action Buttons - fixed at bottom */}
          {isEditing && (
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: 'white',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                zIndex: 9,
                gap: 2,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'text.primary',
                  borderColor: 'divider',
                  py: 0.75,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  // Validate before showing confirmation
                  if (validateFields && !validateFields()) {
                    return; // Stop if validation fails
                  }
                  setOpenConfirm(true);
                }}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  py: 0.75,
                  px: 3,
                  '&:hover': {
                    bgcolor: '#0a1f3a',
                  },
                }}
              >
                Save Changes{' '}
                {isSubmitting && (
                  <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
                )}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={() => handleEditSubmit()}
        message="Are you sure you want to save the changes?"
      />
      <ConfirmationModal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={handleDeleteUser}
        message="Are you sure you want to delete this user?"
      />
    </ModalMAIQ>
  );
};

interface UserDetailsTabProps {
  open: boolean;
  onClose: () => void;
  data: any;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  user: any;
  setUser: React.Dispatch<React.SetStateAction<any>>;
  originalData: any;
  setOriginalData: React.Dispatch<React.SetStateAction<any>>;
  openConfirm: boolean;
  setOpenConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  openDeleteConfirm: boolean;
  setOpenDeleteConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTab: number;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
  onValidateAndSubmit: (validateFields: () => boolean) => void;
  onSetValidateFunction: (validateFn: () => boolean) => void;
}

export const UserDetailsTab: React.FC<UserDetailsTabProps> = ({
  open,
  onClose,
  data,
  setRefreshKey,
  isEditing,
  setIsEditing,
  user,
  setUser,
  originalData,
  setOriginalData,
  openConfirm,
  setOpenConfirm,
  isSubmitting,
  setIsSubmitting,
  openDeleteConfirm,
  setOpenDeleteConfirm,
  selectedTab,
  setSelectedTab,
  onValidateAndSubmit,
  onSetValidateFunction,
}) => {
  const { dealerGroups, getAllDealerGroups } = useDealerGroup();
  const { roles, getAllRoles } = useRole();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [error, setError] = useState<string>('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Inject header reducer and saga
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get dealers from header Redux state
  const dealersListDetails = useSelector(selectDealersList);
  const dealers = dealersListDetails.data?.body?.items || [];

  useEffect(() => {
    if (open) {
      if (roles.length === 0) getAllRoles();
    }
  }, [open]);

  useEffect(() => {
    // Patch: If dealer exists but dealer_id is missing, set dealer_id
    let patchedData = { ...data };
    if (patchedData.dealer && !patchedData.dealer_id) {
      patchedData.dealer_id = patchedData.dealer;
    }
    setUser(patchedData);
    setOriginalData(patchedData);
    // Clear validation errors when data changes
    setValidationErrors({});

    // Set initial tab based on user data
    if (
      patchedData &&
      patchedData.roles &&
      patchedData.roles.length > 0 &&
      (patchedData.roles[0] === 'dealership_executive' || patchedData.roles[0] === 'ai_dealership_executive')
    ) {
      // For dealership_executive role, determine which tab to show based on available data
      if (patchedData.dealer_id && patchedData.dealer_id !== '') {
        setSelectedTab(0); // All dealerships tab
      } else if (
        patchedData.dealer_group_id &&
        patchedData.dealer_group_id !== ''
      ) {
        setSelectedTab(1); // Dealership Lists tab
      } else {
        // Default to All dealerships tab if no data is available
        setSelectedTab(0);
      }
    }
  }, [data]);

  useEffect(() => {
    if (user.roles && user.roles.length > 0) {
      setIsLoadingData(true);
      if (user.roles[0] === 'dealership_executive' || user.roles[0] === 'ai_dealership_executive') {
        // For dealership_executive or ai_dealership_executive role, fetch both dealers and dealer groups
        dispatch(headerActions.requestDealers());
        getAllDealerGroups(user.roles[0]);
      } else {
        getAllDealerGroups(user.roles[0]);
      }
    }
  }, [user.roles]);

  // Add effect to fetch dealers when modal opens for dealership_executive or ai_dealership_executive users
  useEffect(() => {
    if (
      open &&
      user.roles &&
      user.roles.length > 0 &&
      (user.roles[0] === 'dealership_executive' || user.roles[0] === 'ai_dealership_executive')
    ) {
      dispatch(headerActions.requestDealers());
    }
  }, [open, user.roles]);

  // Reset loading state when data is loaded
  useEffect(() => {
    if (hasRequiredData()) {
      setIsLoadingData(false);
    }
  }, [dealers, dealerGroups]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
    setError('');
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleChange = (event: any) => {
    setUser(prev => ({ ...prev, roles: [event.target.value] }));
    setError('');
    // Clear validation error for roles field
    if (validationErrors.roles) {
      setValidationErrors(prev => ({
        ...prev,
        roles: ''
      }));
    }
  };

  const handleDealerGroupChange = (event: any) => {
    const value = event.target.value;
    if (isDealershipExecutive && selectedTab === 0) {
      // When dealership_executive role and "All dealerships" tab is selected, store dealer ID
      setUser(prev => ({
        ...prev,
        dealer_id: value,
        dealer_group_id: '', // Clear dealer_group_id when dealer is selected
      }));
    } else {
      // For dealer groups or other roles, store dealer_group_id
      setUser(prev => ({
        ...prev,
        dealer_group_id: value,
        dealer_id: '', // Clear dealer_id when dealer_group_id is selected
      }));
    }
    // Clear validation error for dealer/dealer_group field
    if (validationErrors.dealer_selection) {
      setValidationErrors(prev => ({
        ...prev,
        dealer_selection: ''
      }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    // Reset both dealer_id and dealer_group_id when tab changes
    setUser(prev => ({
      ...prev,
      dealer_group_id: '',
      dealer_id: '',
    }));
  };

  // Check if current role is dealership_executive or ai_dealership_executive
  const isDealershipExecutive =
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0] === 'dealership_executive' || user.roles[0] === 'ai_dealership_executive');

  // Get the appropriate options based on selected tab
  const getDropdownOptions = () => {
    if (!isDealershipExecutive) {
      return dealerGroups;
    }

    if (selectedTab === 0) {
      // All dealerships tab - show dealers from header API
      return dealers;
    } else {
      // Dealership Lists tab - show dealer groups
      return dealerGroups;
    }
  };

  // Get the appropriate label based on selected tab
  const getDropdownLabel = () => {
    if (!isDealershipExecutive) {
      return 'Select dealer group';
    }

    if (selectedTab === 0) {
      return 'Select dealer';
    } else {
      return 'Select dealer group';
    }
  };

  // Get the appropriate render value based on selected tab
  const getRenderValue = () => {
    if (!isDealershipExecutive) {
      return 'name';
    }

    if (selectedTab === 0) {
      return 'name';
    } else {
      return 'name';
    }
  };

  // Get the appropriate selected value based on selected tab
  const getSelectedValue = () => {
    if (!isDealershipExecutive) {
      return '_id';
    }

    if (selectedTab === 0) {
      return '_id';
    } else {
      return '_id';
    }
  };

  // Get the current value to display in the dropdown
  const getCurrentValue = () => {
    if (!isDealershipExecutive) {
      return user.dealer_group_id || '';
    }

    if (selectedTab === 0) {
      return user.dealer_id || '';
    } else {
      return user.dealer_group_id || '';
    }
  };

  // Get the display value (name) for the current selection
  const getDisplayValue = () => {
    const currentValue = getCurrentValue();
    const options = getDropdownOptions();

    if (!currentValue || !options || options.length === 0) {
      return '';
    }

    const selectedOption = options.find(option => option._id === currentValue);
    return selectedOption ? selectedOption.name : '';
  };

  // Check if we have the required data loaded
  const hasRequiredData = () => {
    if (!isDealershipExecutive) {
      return dealerGroups && dealerGroups.length > 0;
    }

    if (selectedTab === 0) {
      return dealers && dealers.length > 0;
    } else {
      return dealerGroups && dealerGroups.length > 0;
    }
  };

  // Validation function for required fields
  const validateRequiredFields = () => {
    const errors: {[key: string]: string} = {};
    
    // Check required fields
    if (!user.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!user.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!user.email?.trim()) {
      errors.email = 'Email is required';
    }
    
    if (!user.status) {
      errors.status = 'Account status is required';
    }
    
    if (!user.roles || user.roles.length === 0 || !user.roles[0]) {
      errors.roles = 'Role is required';
    }
    
    // Check dealer/dealer_group selection based on role and tab
    if (user.roles && user.roles.length > 0) {
      if (isDealershipExecutive) {
        if (selectedTab === 0) {
          // All dealerships tab - dealer_id is required
          if (!user.dealer_id) {
            errors.dealer_selection = 'Please select a dealer';
          }
        } else {
          // Dealership Lists tab - dealer_group_id is required
          if (!user.dealer_group_id) {
            errors.dealer_selection = 'Please select a dealer group';
          }
        }
      } else {
        // Other roles - dealer_group_id is required
        if (!user.dealer_group_id) {
          errors.dealer_selection = 'Please select a dealer group';
        }
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Set validation function in parent component
  useEffect(() => {
    onSetValidateFunction(validateRequiredFields);
  }, [user, selectedTab, isDealershipExecutive]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        sx={{
          p: 3,
          flex: 1,
          overflowY: 'auto',
          pb: isEditing ? 10 : 3,
        }}
      >
        <Grid container spacing={2.5}>
          {/* Name Fields */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              First Name {isEditing && '*'}
            </Typography>
            <TextField
              fullWidth
              name="first_name"
              value={user.first_name || ''}
              onChange={handleChange}
              disabled={!isEditing}
              variant="outlined"
              size="small"
              placeholder="Enter first name"
              required={isEditing}
              error={!!validationErrors.first_name}
              helperText={validationErrors.first_name}
              inputProps={{
                sx: { py: 1.25 },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Last Name {isEditing && '*'}
            </Typography>
            <TextField
              fullWidth
              name="last_name"
              value={user.last_name || ''}
              onChange={handleChange}
              disabled={!isEditing}
              variant="outlined"
              size="small"
              placeholder="Enter last name"
              required={isEditing}
              error={!!validationErrors.last_name}
              helperText={validationErrors.last_name}
              inputProps={{
                sx: { py: 1.25 },
              }}
            />
          </Grid>

          {/* Email & ZIP */}
          <Grid item xs={12} sm={8}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Email {isEditing && '*'}
            </Typography>
            <TextField
              fullWidth
              name="email"
              type="email"
              value={user.email || ''}
              onChange={handleChange}
              disabled={!isEditing}
              variant="outlined"
              size="small"
              placeholder="Enter email address"
              required={isEditing}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              inputProps={{
                sx: { py: 1.25 },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              ZIP Code
            </Typography>
            <TextField
              fullWidth
              name="zip"
              value={user.zip || ''}
              onChange={handleChange}
              disabled={!isEditing}
              variant="outlined"
              size="small"
              placeholder="Enter ZIP code"
              inputProps={{
                sx: { py: 1.25 },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Account Status {isEditing && '*'}
            </Typography>
            <Select
              fullWidth
              name="status"
              value={user.status || 'INACTIVE'}
              onChange={handleChange as any}
              disabled={!isEditing}
              size="small"
              error={!!validationErrors.status}
              sx={{
                height: '40px',
              }}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
            {validationErrors.status && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {validationErrors.status}
              </Typography>
            )}
          </Grid>

          {/* Roles Dropdown */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Role {isEditing && '*'}
            </Typography>
            <Box
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '8px 14px',
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, 16px) scale(1)',
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)',
                },
                '& .MuiFormControl-root': {
                  marginTop: 0,
                },
              }}
            >
              <SelectMAIQ
                label="Select role"
                value={
                  (user.roles && Array.isArray(user.roles)
                    ? user.roles[0]
                    : user.roles) || ''
                }
                options={roles}
                onChange={handleRoleChange}
                disabled={!isEditing}
                selectedValue="name"
                renderValue="name"
              />
            {validationErrors.roles && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {validationErrors.roles}
              </Typography>
            )}
            </Box>
          </Grid>

          {/* Dealer Group Dropdown */}
          {user.roles && user.roles.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                {isDealershipExecutive
                  ? 'Dealership Selection'
                  : 'Dealer Group'}{' '}
                {isEditing && '*'}
              </Typography>

              {/* Tabs for dealership_executive role */}
              {isDealershipExecutive && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={selectedTab}
                    onChange={isEditing ? handleTabChange : undefined}
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 40,
                        fontSize: '0.875rem',
                        flex: 1,
                      },
                      '& .MuiTabs-flexContainer': {
                        width: '100%',
                      },
                    }}
                  >
                    <Tab label="All dealerships" />
                    <Tab label="Dealership Lists" />
                  </Tabs>
                </Box>
              )}

              {isLoadingData ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.primary">
                    Loading{' '}
                    {isDealershipExecutive ? 'dealerships' : 'dealer groups'}...
                  </Typography>
                </Box>
              ) : hasRequiredData() ? (
                <Box
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      padding: '8px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      transform: 'translate(14px, 16px) scale(1)',
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)',
                    },
                    '& .MuiFormControl-root': {
                      marginTop: 0,
                    },
                  }}
                >
                  <SelectMAIQ
                    key={`${selectedTab}-${
                      getDropdownOptions().length
                    }-${getCurrentValue()}`}
                    label={getDropdownLabel()}
                    value={getCurrentValue() || ''}
                    options={getDropdownOptions()}
                    onChange={handleDealerGroupChange}
                    disabled={!isEditing}
                    renderValue={getRenderValue()}
                    selectedValue={getSelectedValue()}
                  />
                  {validationErrors.dealer_selection && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {validationErrors.dealer_selection}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.primary">
                  No {isDealershipExecutive ? 'dealerships' : 'dealer groups'}{' '}
                  available
                </Typography>
              )}
            </Grid>
          )}

          {/* Metadata section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              System Information
            </Typography>
          </Grid>

          {/* Created & Updated At (Read-Only) */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}
            >
              <HistoryIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.primary">
                Created At
              </Typography>
            </Box>
            <Typography variant="body2">
              {formatToLocalTime(user.created_at)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}
            >
              <HistoryIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.primary">
                Updated At
              </Typography>
            </Box>
            <Typography variant="body2">
              {formatToLocalTime(user.updated_at)}
            </Typography>
          </Grid>

          {/* Password Reset Section */}
          <Grid item xs={12}>
            <PasswordResetSection userId={user._id} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default UserDetailsModal;
